import React, { useEffect, useMemo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Inline, Stack } from '@atlaskit/primitives/compiled';
import Heading from '@atlaskit/heading';
import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import type { RecommendationsList$key } from '@atlassian/jira-relay/src/__generated__/RecommendationsList.graphql';
import { useAnalyticsEvents, fireTrackAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { useIntl } from '@atlassian/jira-intl';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';
import { RecommendationCard } from './recommendation-card/RecommendationCard.tsx';
import { countRecommendationsByType, getFirstInviterProjectId } from './analytics.tsx';

const styles = cssMap({
	wrapper: {
		marginBottom: token('space.500'),
	},
});

type Props = {
	recommendationsRef: RecommendationsList$key;
};

export const RecommendationsList: React.FC<Props> = ({ recommendationsRef }) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = useFragment<RecommendationsList$key>(
		graphql`
			fragment RecommendationsList on JiraProjectRecommendationConnection {
				edges {
					node {
						details {
							recommendationType @required(action: THROW)
						}
						project {
							projectId @required(action: THROW)
						}
						...RecommendationCard_recommendation
					}
				}
			}
		`,
		recommendationsRef,
	);

	const recommendations = useMemo(
		() => data.edges?.map((edge) => edge?.node).filter(Boolean) ?? [],
		[data.edges],
	);

	useEffect(() => {
		if (recommendations.length > 0) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'jiraProjectDirectoryRecommendations viewed', {
				recommendationsCount: recommendations.length,
				inviterRecommendationsCount: countRecommendationsByType(
					recommendations,
					'INVITER_ACTIVITY',
				),
				tenantRecommendationsCount: countRecommendationsByType(recommendations, 'TENANT_ACTIVITY'),
				inviterProjectId: getFirstInviterProjectId(recommendations),
			});
		}
	}, [createAnalyticsEvent, recommendations]);

	if (recommendations.length === 0) {
		return null;
	}

	return (
		<Stack xcss={styles.wrapper} space="space.100">
			<Heading size="small" as="h2">
				{fg('project_term_refresh_ga')
					? formatMessage(messages.recommendedProjectsHeadingSpaces)
					: formatMessage(messages.recommendedProjectsHeading)}
			</Heading>
			<Inline space="space.100" grow="fill">
				{recommendations.map((recommendation) => {
					const projectId = recommendation.project?.projectId;
					const recommendationType = recommendation.details?.recommendationType;
					return (
						<RecommendationCard
							key={`${projectId}-${recommendationType}`}
							recommendationRef={recommendation}
						/>
					);
				})}
			</Inline>
		</Stack>
	);
};
